import { useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, hlaskaZChyby } from '../../api/client.js';
import type {
  DenikPamet,
  DenikZaznam,
  DenikZaznamDetail,
  LideNaStavbeFirma,
} from '../../api/types.js';
import { useAuth } from '../../auth/AuthContext.js';
import {
  CheckCircle2,
  AlertTriangle,
  Camera,
  ChevronLeft,
  ChevronRight,
  Hammer,
  Images,
  Megaphone,
  Mic,
  Package,
  Pencil,
  PenLine,
  RotateCcw,
  Square,
  Sun,
  Truck,
  Users,
} from 'lucide-react';
import type { TerenKontext } from '../../components/TerenLayout.js';
import {
  Badge,
  Btn,
  Callout,
  Chyba,
  Input,
  Nacitani,
  PoleKarta,
  Sekce,
  Textarea,
  VelkeTlacitko,
} from '../../components/ui.js';
import { useDiktovani } from '../../lib/diktovani.js';
import { formatDen } from '../../lib/format.js';
import { useMojeNalezy } from '../../lib/mojeNalezy.js';
import { jeOnline, odesliNeboZarad } from '../../lib/offline.js';
import { denVTydnu, formatDenSTydnem } from './datum.js';
import { HeadcountKarta } from './HeadcountKarta.js';
import { normalizujLidi } from './lide.js';
import { NalezWizard } from './NalezWizard.js';
import { OnboardingKarta } from './OnboardingKarta.js';
import { PodpisDialog } from './PodpisDialog.js';
import { VyzvaWizard } from './VyzvaWizard.js';
import { sledujUdalost } from '../../lib/analytika.js';

export { normalizujLidi } from './lide.js';

/**
 * Den mistra (wireframe 13 §1) — stěžejní terénní obrazovka: auto-založený
 * den s počasím, diktát, foto, kopie včerejška, lidé na stavbě, NÁLEZ,
 * VÝZVA TDI, podpis = zámek. Vše ≤3 ťuknutí, jeden sloupec, velká tlačítka.
 */

const klicSnapshotu = (zakazkaId: string) => `helfr.denik.snapshot.${zakazkaId}`;

function ulozSnapshot(zakazkaId: string, zaznam: DenikZaznamDetail): void {
  try {
    localStorage.setItem(klicSnapshotu(zakazkaId), JSON.stringify(zaznam));
  } catch {
    // plné úložiště — cache je bonus
  }
}

function nactiSnapshot(zakazkaId: string): DenikZaznamDetail | null {
  try {
    const raw = localStorage.getItem(klicSnapshotu(zakazkaId));
    return raw ? (JSON.parse(raw) as DenikZaznamDetail) : null;
  } catch {
    return null;
  }
}

/** Poloha pro auto-počasí (A4) — krátký timeout, selhání nikdy neblokuje. */
function zjistiPolohu(): Promise<{ lat: number; lon: number } | null> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return resolve(null);
    const hotovo = (v: { lat: number; lon: number } | null) => resolve(v);
    const timer = setTimeout(() => hotovo(null), 3000);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        clearTimeout(timer);
        hotovo({ lat: p.coords.latitude, lon: p.coords.longitude });
      },
      () => {
        clearTimeout(timer);
        hotovo(null);
      },
      { timeout: 2500, maximumAge: 10 * 60 * 1000 },
    );
  });
}

export function DenikPage() {
  const { zakazka } = useOutletContext<TerenKontext>();
  const { id: paramId } = useParams();
  const queryClient = useQueryClient();
  const { me, maPravo } = useAuth();
  const { nalezy: mojeNalezy } = useMojeNalezy(true);
  const [chyba, setChyba] = useState<string | null>(null);
  const [wizard, setWizard] = useState<'nalez' | 'vyzva' | null>(null);
  const [podpisDialog, setPodpisDialog] = useState(false);

  const klicZaznamu = paramId ? ['denik-zaznam', paramId] : ['denik-dnes', zakazka.id];

  const dotaz = useQuery({
    queryKey: klicZaznamu,
    queryFn: async (): Promise<DenikZaznamDetail> => {
      if (paramId) return api.get<DenikZaznamDetail>(`/api/diary/${paramId}`);
      const poloha = await zjistiPolohu();
      const params = new URLSearchParams({ zakazkaId: zakazka.id });
      if (poloha) {
        params.set('lat', String(poloha.lat));
        params.set('lon', String(poloha.lon));
      }
      const dnes = await api.get<DenikZaznamDetail>(`/api/diary/today?${params.toString()}`);
      ulozSnapshot(zakazka.id, dnes);
      return dnes;
    },
  });

  // Offline čtení (G1): spadl dotaz a nejsme online → poslední načtený den.
  const snapshot = useMemo(
    () => (dotaz.error && !paramId && !jeOnline() ? nactiSnapshot(zakazka.id) : null),
    [dotaz.error, paramId, zakazka.id],
  );
  const zaznam = dotaz.data ?? snapshot ?? undefined;

  const { data: dny } = useQuery({
    queryKey: ['denik-dny', zakazka.id],
    queryFn: () => api.get<DenikZaznam[]>(`/api/diary?zakazkaId=${encodeURIComponent(zakazka.id)}`),
  });

  const { data: pamet } = useQuery({
    queryKey: ['denik-pamet', zakazka.id],
    queryFn: () =>
      api.get<DenikPamet>(`/api/diary/memory?zakazkaId=${encodeURIComponent(zakazka.id)}`),
  });

  const obnov = () => {
    void queryClient.invalidateQueries({ queryKey: klicZaznamu });
    void queryClient.invalidateQueries({ queryKey: ['denik-dny', zakazka.id] });
  };

  // Viditelné potvrzení zápisu — tichý autosave nevzbuzuje důvěru („je to
  // tam?" je důvod vrátit se k papíru). Offline říká pravdu: čeká na odeslání.
  const [ulozeno, setUlozeno] = useState<string | null>(null);

  /** PATCH sekce dne — offline jde do fronty a lokální stav zůstává vidět. */
  const uloz = async (telo: Record<string, unknown>): Promise<void> => {
    if (!zaznam) return;
    setChyba(null);
    try {
      const hned = await odesliNeboZarad({
        druh: 'json',
        metoda: 'PATCH',
        url: `/api/diary/${zaznam.id}`,
        telo,
        popis: `Deník ${formatDen(zaznam.datum)} — zápis`,
      });
      const novy = { ...zaznam, ...telo } as DenikZaznamDetail;
      queryClient.setQueryData(klicZaznamu, novy);
      if (!paramId) ulozSnapshot(zakazka.id, novy);
      setUlozeno(
        hned
          ? `Uloženo ${new Date().toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}`
          : 'Uloženo do fronty — odešle se, až bude signál',
      );
      if (hned) obnov();
    } catch (err) {
      setChyba(hlaskaZChyby(err));
    }
  };

  // Kopie včerejška (A2) vrací už zkopírovaný záznam — rovnou do cache; karty
  // lidí, strojů a materiálu drží text v lokálním stavu, proto se přes klíč
  // s číslem kopie remountnou na čerstvá data.
  const [kopieVerze, setKopieVerze] = useState(0);
  const kopie = useMutation({
    mutationFn: () =>
      api.post<DenikZaznamDetail>(`/api/diary/${zaznam?.id}/copy-from-previous`, {}),
    onSuccess: (novy) => {
      queryClient.setQueryData(klicZaznamu, novy);
      setKopieVerze((v) => v + 1);
      obnov();
    },
    onError: (err) => setChyba(hlaskaZChyby(err)),
  });

  const podpis = useMutation({
    mutationFn: (p: { obrazek: string; jmeno: string }) =>
      api.post<DenikZaznamDetail>(`/api/diary/${zaznam?.id}/sign`, { podpis: p }),
    onSuccess: () => {
      sledujUdalost('denik_podepsan');
      setPodpisDialog(false);
      obnov();
    },
    onError: (err) => {
      setPodpisDialog(false);
      setChyba(hlaskaZChyby(err));
    },
  });

  if (dotaz.isLoading) return <Nacitani text="Otevírám dnešní den…" />;
  if (!zaznam) {
    return (
      <Chyba>
        {jeOnline()
          ? hlaskaZChyby(dotaz.error)
          : 'Bez připojení a žádný dřív načtený den — deník se otevře po připojení.'}
      </Chyba>
    );
  }

  const uzamceno = zaznam.uzamceno;
  const jePodepisujici = maPravo('denik.sign');

  // Nepodepsaný minulý den (B4, edge case wireframu): žlutý proužek s [Otevřít].
  const nepodepsanyMinuly = dny?.find(
    (d) =>
      d.nepodepsany &&
      d.id !== zaznam.id &&
      d.typ === 'stavebni' &&
      !d.opravujeZaznamId &&
      d.datum < zaznam.datum,
  );

  // Zpětná vazba mistrovi (A6.1): zamítnutý nález ohlásit přímo v deníku, ať se
  // smyčka „hlásím a nic se neděje" neztratí v Menu. Nejnovější zamítnutý první.
  const zamitnutyNalez = (mojeNalezy ?? [])
    .filter((n) => n.stav === 'zamitnut')
    .sort((a, b) => b.kdy.localeCompare(a.kdy))[0];

  return (
    // Spodní odsazení: nad spodní navigací plave akční pás (NÁLEZ + stav dne).
    <div className="flex flex-col gap-4 pb-20">
      {/* Onboarding mistra (C1 / TOP 8) — jen na dnešku, jednorázově. */}
      {!paramId ? <OnboardingKarta /> : null}

      {/* Hlavička DENÍKU = listování dny (‹ den ›) — deníkový pocit: dny
          nejsou schované v harmonice, dnešek je jasně označený a starší dny
          jsou na jedno ťuknutí šipkou. */}
      <DenNavigace zaznam={zaznam} dny={dny} paramId={paramId} />
      {/* Počasí = samozřejmost dne — nevýrazný řádek pod datem, ne úkol. */}
      <PocasiRadek key={`pocasi-${zaznam.id}-${uzamceno}`} zaznam={zaznam} uloz={uloz} />

      {snapshot && !dotaz.data ? (
        <div className="rounded-lg border border-border-strong bg-fill-disabled p-3 text-base">
          Bez připojení — zobrazuji poslední načtený stav. Zápisy se uloží do fronty.
        </div>
      ) : null}

      {zamitnutyNalez ? (
        <Callout ton="error">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>
              Váš nález z {formatDen(zamitnutyNalez.kdy)} byl zamítnut — důvod v Moje nálezy.
            </span>
            <Link
              to="/teren/moje-nalezy"
              className="min-h-12 shrink-0 py-3 font-semibold text-primary underline underline-offset-2"
            >
              Zobrazit
            </Link>
          </div>
        </Callout>
      ) : null}

      {nepodepsanyMinuly ? (
        <Callout ton="warning">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>
              {formatDenSTydnem(nepodepsanyMinuly.datum)} není podepsaný — dopište a podepište ho.
            </span>
            <Link
              to={`/teren/denik/${nepodepsanyMinuly.id}`}
              className="inline-flex min-h-12 items-center rounded-lg bg-accent px-4 font-semibold text-text-heading"
            >
              Otevřít
            </Link>
          </div>
        </Callout>
      ) : null}

      {uzamceno ? (
        <div
          role="alert"
          className="rounded-lg border border-success-border bg-success-bg p-3 text-base text-success-text"
        >
          Den je podepsaný a uzamčený
          {zaznam.podepsal ? ` (${zaznam.podepsal.jmeno})` : ''} — už ho nelze měnit. Oprava jde jen
          opravným záznamem; nález a výzva fungují dál.
        </div>
      ) : null}

      {chyba ? <Chyba>{chyba}</Chyba> : null}
      {ulozeno && !chyba && !uzamceno ? (
        <p
          aria-live="polite"
          className="flex items-center gap-1.5 text-sm font-semibold text-success-text"
        >
          <CheckCircle2 size={15} strokeWidth={2} aria-hidden="true" /> {ulozeno}
        </p>
      ) : null}

      {/* Mistrova hlavní věc: Provedené práce (jediné primární diktovací CTA),
          Lidé, Fotky. Počasí/Stroje/Materiál jsou doplněk — sbalené níže. */}
      <PraceKarta
        key={`prace-${zaznam.id}-${uzamceno}`}
        zaznam={zaznam}
        uloz={uloz}
        poNahrani={obnov}
        naChybu={setChyba}
      />
      {/* NA STAVBĚ DNES — evidence dne (lidé, BOZP počty, stroje, materiál)
          jasně oddělená od zápisu prací. Převzetí stojí v čele skupiny
          s viditelným vysvětlením, co přesně přenáší. */}
      <section className="mt-2 flex flex-col gap-3">
        <p className="px-1 text-xs font-semibold tracking-wider text-text-secondary uppercase">
          Na stavbě dnes
        </p>
        {!uzamceno ? (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-surface p-3">
            <div className="min-w-0">
              <p className="text-base font-semibold">Stejné jako včera?</p>
              <p className="text-sm text-text-secondary">
                Převezme lidi, stroje a materiál. Práce se píší znovu.
              </p>
            </div>
            <Btn varianta="secondary" onClick={() => kopie.mutate()} disabled={kopie.isPending}>
              <RotateCcw size={16} strokeWidth={2} aria-hidden="true" />
              {kopie.isPending ? 'Přebírám…' : 'PŘEVZÍT'}
            </Btn>
          </div>
        ) : null}
        <LideKarta
          key={`lide-${zaznam.id}-${uzamceno}-${kopieVerze}`}
          zaznam={zaznam}
          pamet={pamet}
          uloz={uloz}
        />
        <Sekce titulek="Počty lidí od subek" podtitulek="jen evidence pro BOZP">
          <HeadcountKarta zakazkaId={zakazka.id} pamet={pamet} naChybu={setChyba} />
        </Sekce>
        <Sekce titulek="Stroje a materiál">
          <DiktovanaKarta
            key={`stroje-${zaznam.id}-${uzamceno}-${kopieVerze}`}
            nadpis="Stroje a mechanizace"
            ikona={<Truck size={16} strokeWidth={1.75} />}
            pole="mechanizace"
            zaznam={zaznam}
            pamet={pamet?.stroje ?? []}
            placeholder="Jeřáb, čerpadlo, výtah…"
            uloz={uloz}
          />
          <DiktovanaKarta
            key={`material-${zaznam.id}-${uzamceno}-${kopieVerze}`}
            nadpis="Materiál"
            ikona={<Package size={16} strokeWidth={1.75} />}
            pole="material"
            zaznam={zaznam}
            pamet={pamet?.materialy ?? []}
            placeholder="Beton, cihly, výztuž…"
            uloz={uloz}
          />
        </Sekce>
      </section>

      {/* Výzvy u záznamu — stav otevřeno/vyjádřeno svítí přímo u dne (E1). */}
      {zaznam.vyzvy.length > 0 && (
        <PoleKarta
          ikona={<Megaphone size={16} strokeWidth={1.75} />}
          nadpis="Výzvy k tomuto dni"
          vaha="jemny"
        >
          <ul className="flex flex-col gap-2">
            {zaznam.vyzvy.map((v) => (
              <li key={v.id} className="flex items-start justify-between gap-2 text-base">
                <span className="min-w-0">
                  {v.predmetTyp === 'prejimka' && (
                    <span className="mr-1 rounded bg-warning-bg px-1.5 py-0.5 text-xs font-bold text-warning-text">
                      PŘEJÍMKA
                    </span>
                  )}
                  {v.adresatUser?.jmeno ?? v.adresatRole} — {v.text}
                </span>
                <Badge stav={v.stav === 'vyzvano' ? 'warning' : 'success'}>
                  {v.stav === 'vyzvano' ? 'otevřeno' : 'vyjádřeno'}
                </Badge>
              </li>
            ))}
          </ul>
        </PoleKarta>
      )}

      {/* VÝZVA TDI je v plovoucím akčním pásu dole (vždy po ruce, i na
          podepsaném dni) — není to součást textu zápisu. */}

      {uzamceno ? <OpravnyZaznam zaznam={zaznam} naChybu={setChyba} /> : null}

      {/* Plovoucí akční pás (A1.1 / TOP 2): NÁLEZ + stav dne trvale nad ohybem. */}
      <TerenAkcniPas
        uzamceno={uzamceno}
        jePodepisujici={jePodepisujici}
        naNalez={() => setWizard('nalez')}
        naVyzvu={() => setWizard('vyzva')}
        naPodpis={() => setPodpisDialog(true)}
      />

      {wizard === 'nalez' ? (
        <NalezWizard
          zakazkaId={zakazka.id}
          objekty={zakazka.objekty ?? []}
          zavri={(hotovo) => {
            setWizard(null);
            if (hotovo) obnov();
          }}
        />
      ) : null}
      {wizard === 'vyzva' ? (
        <VyzvaWizard
          zaznam={zaznam}
          zavri={(hotovo) => {
            setWizard(null);
            if (hotovo) obnov();
          }}
        />
      ) : null}

      {podpisDialog ? (
        <PodpisDialog
          zaznam={zaznam}
          probiha={podpis.isPending}
          jmeno={me?.jmeno ?? ''}
          onPotvrdit={(p) => podpis.mutate(p)}
          onZrusit={() => setPodpisDialog(false)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Navigace dny deníku: ‹ šipky › mezi existujícími dny, uprostřed velké datum
// s chipem DNES / „starší den" + rychlý návrat na dnešek. Nahrazuje schovaný
// seznam „Dny deníku" v harmonice — deník, ve kterém se listuje.
// ---------------------------------------------------------------------------

const DEN_SIPKA =
  'flex size-12 shrink-0 items-center justify-center rounded-xl border border-border bg-surface text-text-primary shadow-sm transition-colors duration-100 hover:bg-bg';

function DenNavigace({
  zaznam,
  dny,
  paramId,
}: {
  zaznam: DenikZaznamDetail;
  dny: DenikZaznam[] | undefined;
  paramId: string | undefined;
}) {
  // Dny řadí API od nejnovějšího; index aktuálně zobrazeného dne.
  const seznam = dny ?? [];
  const idx = seznam.findIndex((d) => d.id === zaznam.id);
  const starsi = idx >= 0 ? seznam[idx + 1] : undefined;
  const novejsi = idx > 0 ? seznam[idx - 1] : undefined;
  // Nejnovější den = dnešek (auto-zakládá se při otevření) → kanonická routa.
  const cilDne = (d: DenikZaznam) =>
    d.id === seznam[0]?.id ? '/teren/denik' : `/teren/denik/${d.id}`;
  const jeDnesek = !paramId || zaznam.id === seznam[0]?.id;

  return (
    <nav aria-label="Listování dny deníku" className="flex items-center gap-3">
      {starsi ? (
        <Link to={cilDne(starsi)} aria-label="Starší den" className={DEN_SIPKA}>
          <ChevronLeft size={22} strokeWidth={2} aria-hidden="true" />
        </Link>
      ) : (
        <span className={`${DEN_SIPKA} opacity-30`} aria-hidden="true">
          <ChevronLeft size={22} strokeWidth={2} />
        </span>
      )}

      <h1 className="min-w-0 flex-1 text-center">
        <span className="block text-xl leading-tight font-bold">
          {denVTydnu(zaznam.datum)} {formatDen(zaznam.datum)}
        </span>
        {jeDnesek ? (
          <span className="mt-0.5 inline-block rounded-full bg-accent px-2.5 py-0.5 text-xs font-bold text-text-heading">
            DNES
          </span>
        ) : (
          <span className="mt-0.5 flex items-center justify-center gap-2 text-sm">
            <span className="text-text-secondary">starší den</span>
            <Link
              to="/teren/denik"
              className="font-semibold text-primary underline underline-offset-2"
            >
              Dnešek →
            </Link>
          </span>
        )}
        {zaznam.opravujeZaznam ? (
          <span className="block text-sm font-normal text-text-secondary">
            opravný záznam ke dni {formatDen(zaznam.opravujeZaznam.datum)}
          </span>
        ) : null}
      </h1>

      {novejsi ? (
        <Link to={cilDne(novejsi)} aria-label="Novější den" className={DEN_SIPKA}>
          <ChevronRight size={22} strokeWidth={2} aria-hidden="true" />
        </Link>
      ) : (
        <span className={`${DEN_SIPKA} opacity-30`} aria-hidden="true">
          <ChevronRight size={22} strokeWidth={2} />
        </span>
      )}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Akční pás (A1.1 / TOP 2): plovoucí lišta nad spodní navigací se dvěma trvale
// dosažitelnými akcemi — NÁLEZ (akcent, vlevo) a stav dne (vpravo). Klíčové
// momenty POC už nejsou 3–4 obrazovky pod ohybem.
// ---------------------------------------------------------------------------

function TerenAkcniPas({
  uzamceno,
  jePodepisujici,
  naNalez,
  naVyzvu,
  naPodpis,
}: {
  uzamceno: boolean;
  jePodepisujici: boolean;
  naNalez: () => void;
  naVyzvu: () => void;
  naPodpis: () => void;
}) {
  return (
    <div className="fixed inset-x-0 bottom-16 z-30 mx-auto max-w-2xl px-2 pb-2">
      <div className="flex items-center gap-2 rounded-xl border border-border bg-surface p-2 shadow-lg">
        <button
          type="button"
          className="flex min-h-12 flex-1 items-center justify-center gap-1 rounded-lg bg-accent px-3 text-lg font-bold text-text-heading hover:bg-accent-hover active:bg-accent-pressed"
          onClick={naNalez}
        >
          <AlertTriangle size={20} strokeWidth={2} aria-hidden="true" /> NÁLEZ
        </button>
        {/* VÝZVA TDI vždy po ruce (i na podepsaném dni) — klíčový moment
            „zítra se zakrývá" nesmí být zahrabaný v textu zápisu. */}
        <button
          type="button"
          aria-label="VÝZVA TDI"
          className="flex min-h-12 flex-col items-center justify-center rounded-lg border border-border bg-surface px-2.5 leading-tight text-primary transition-colors duration-100 hover:bg-primary-tint"
          onClick={naVyzvu}
        >
          <Megaphone size={17} strokeWidth={2} aria-hidden="true" />
          <span className="text-[11px] font-bold">VÝZVA</span>
        </button>
        {uzamceno ? (
          <span className="flex min-h-12 items-center rounded-lg bg-success-bg px-3 text-base font-semibold whitespace-nowrap text-success-text">
            ✓ Podepsáno
          </span>
        ) : jePodepisujici ? (
          <button
            type="button"
            className="flex min-h-12 flex-1 items-center justify-center gap-1.5 rounded-lg bg-text-heading px-3 text-base font-bold whitespace-nowrap text-white hover:bg-text-primary"
            onClick={naPodpis}
          >
            <PenLine size={18} strokeWidth={2} aria-hidden="true" /> PODEPSAT
          </button>
        ) : (
          <span className="flex min-h-12 items-center rounded-lg bg-warning-bg px-3 text-base font-semibold whitespace-nowrap text-warning-text">
            Nepodepsáno
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Počasí (A4): předvyplněno automaticky (0 ťuknutí), ruční oprava možná.
// ---------------------------------------------------------------------------

function PocasiRadek({
  zaznam,
  uloz,
}: {
  zaznam: DenikZaznamDetail;
  uloz: (telo: Record<string, unknown>) => Promise<void>;
}) {
  const [upravuji, setUpravuji] = useState(false);
  const [text, setText] = useState(zaznam.pocasi ?? '');

  if (upravuji) {
    return (
      <div className="flex items-center gap-2">
        <Input
          aria-label="Počasí"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="např. 22 °C, polojasno, vítr 3 m/s"
        />
        <Btn
          varianta="secondary"
          className="shrink-0"
          onClick={() => {
            setUpravuji(false);
            void uloz({ pocasi: text || null });
          }}
        >
          OK
        </Btn>
      </div>
    );
  }

  return (
    <p className="-mt-2 flex flex-wrap items-center justify-center gap-x-1.5 text-center text-sm text-text-secondary">
      <Sun size={14} strokeWidth={2} aria-hidden="true" className="shrink-0" />
      {zaznam.pocasi ? (
        <>
          <span>{zaznam.pocasi}</span>
          <span>(auto)</span>
        </>
      ) : (
        <span>počasí se doplní po připojení</span>
      )}
      {!zaznam.uzamceno && (
        <button
          type="button"
          className="min-h-8 px-1 font-semibold text-primary underline underline-offset-2"
          onClick={() => setUpravuji(true)}
        >
          upravit
        </button>
      )}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Lidé na stavbě (A5/D1–D2): počty po firmách, paměť — parta se nezadává denně.
// ---------------------------------------------------------------------------

function LideKarta({
  zaznam,
  pamet,
  uloz,
}: {
  zaznam: DenikZaznamDetail;
  pamet?: DenikPamet;
  uloz: (telo: Record<string, unknown>) => Promise<void>;
}) {
  const [lide, setLide] = useState<LideNaStavbeFirma[]>(() => normalizujLidi(zaznam.lideNaStavbe));
  const [novaFirma, setNovaFirma] = useState('');

  const zmen = (dalsi: LideNaStavbeFirma[]) => {
    setLide(dalsi);
    void uloz({ lideNaStavbe: dalsi });
  };

  const pridej = (nazev: string, subdodavatelId?: string | null) => {
    const n = nazev.trim();
    if (!n || lide.some((l) => l.nazev === n)) return;
    zmen([...lide, { nazev: n, pocet: 1, subdodavatelId: subdodavatelId ?? null }]);
    setNovaFirma('');
  };

  const chybejiciZPameti = (pamet?.firmy ?? []).filter(
    (f) => !lide.some((l) => l.nazev === f.nazev),
  );

  return (
    <PoleKarta ikona={<Users size={16} strokeWidth={1.75} />} nadpis="Lidé na stavbě">
      {lide.length === 0 && (
        <p className="mt-1 text-base text-text-secondary">Zatím nikdo — přidejte firmu níže.</p>
      )}
      <ul className="mt-1 flex flex-col">
        {lide.map((l, i) => (
          <li
            key={l.nazev}
            className="flex min-h-12 items-center justify-between gap-2 border-t border-border first:border-t-0"
          >
            <span className="min-w-0 truncate text-base font-semibold">{l.nazev}</span>
            {zaznam.uzamceno ? (
              <span className="text-base">{l.pocet}</span>
            ) : (
              <span className="flex items-center gap-1">
                <button
                  type="button"
                  aria-label={`Ubrat — ${l.nazev}`}
                  className="min-h-12 min-w-12 rounded-xl border border-border bg-surface text-xl font-bold shadow-sm transition-colors duration-100 hover:bg-bg"
                  onClick={() => {
                    const dalsi = [...lide];
                    if (l.pocet <= 0) return;
                    dalsi[i] = { ...l, pocet: l.pocet - 1 };
                    zmen(dalsi.filter((x) => x.pocet > 0 || x.nazev !== l.nazev));
                  }}
                >
                  −
                </button>
                <span className="min-w-8 text-center text-lg font-bold">{l.pocet}</span>
                <button
                  type="button"
                  aria-label={`Přidat — ${l.nazev}`}
                  className="min-h-12 min-w-12 rounded-xl border border-border bg-surface text-xl font-bold shadow-sm transition-colors duration-100 hover:bg-bg"
                  onClick={() => {
                    const dalsi = [...lide];
                    dalsi[i] = { ...l, pocet: l.pocet + 1 };
                    zmen(dalsi);
                  }}
                >
                  +
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
      {!zaznam.uzamceno && (
        <>
          {chybejiciZPameti.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {chybejiciZPameti.map((f) => (
                <button
                  key={f.nazev}
                  type="button"
                  className="min-h-12 rounded-full border border-border bg-surface px-4 text-base font-semibold shadow-sm transition-colors duration-100 hover:bg-bg"
                  onClick={() => pridej(f.nazev, f.subdodavatelId)}
                >
                  + {f.nazev}
                </button>
              ))}
            </div>
          )}
          <div className="mt-3 flex gap-2">
            <Input
              aria-label="Nová firma"
              placeholder="Další firma…"
              value={novaFirma}
              onChange={(e) => setNovaFirma(e.target.value)}
            />
            <Btn varianta="secondary" className="shrink-0" onClick={() => pridej(novaFirma)}>
              Přidat
            </Btn>
          </div>
        </>
      )}
      {/* Docházka je hned u „lidí" — mistr nehledá hodiny pro výplaty v Menu. */}
      <Link
        to="/teren/dochazka"
        className="mt-3 flex min-h-12 items-center justify-between rounded-lg border border-border bg-bg px-3 text-base font-semibold text-primary transition-colors duration-100 hover:bg-primary-tint"
      >
        Docházka party — hodiny pro výplaty
        <span aria-hidden="true">→</span>
      </Link>
    </PoleKarta>
  );
}

// ---------------------------------------------------------------------------
// Stroje/mechanizace a Materiál (A6 + D3): velké textové pole s diktátem jako
// u prací; chips z paměti posledních dnů = „vložit ≤2 ťuknutí" (tap přidá
// frázi a hned uloží). Po podpisu read-only. Kopie včerejška obě pole plní.
// ---------------------------------------------------------------------------

function DiktovanaKarta({
  nadpis,
  ikona,
  pole,
  zaznam,
  pamet,
  placeholder,
  uloz,
}: {
  nadpis: string;
  ikona: React.ReactNode;
  pole: 'mechanizace' | 'material';
  zaznam: DenikZaznamDetail;
  /** Fráze z paměti (GET /api/diary/memory — stroje/materialy posledních dnů). */
  pamet: string[];
  placeholder: string;
  uloz: (telo: Record<string, unknown>) => Promise<void>;
}) {
  const [text, setText] = useState(zaznam[pole] ?? '');

  const diktovani = useDiktovani((useky) => {
    setText((puvodni) => {
      const dalsi = puvodni ? `${puvodni.trimEnd()} ${useky}` : useky;
      void uloz({ [pole]: dalsi });
      return dalsi;
    });
  });

  // Nabídka z paměti (D3): jen fráze, které v poli ještě nejsou.
  const nabidka = pamet.filter((f) => !text.includes(f)).slice(0, 6);

  const vlozFrazi = (fraze: string) => {
    const dalsi = text ? `${text.trimEnd().replace(/[,;]$/, '')}, ${fraze}` : fraze;
    setText(dalsi);
    void uloz({ [pole]: dalsi });
  };

  if (zaznam.uzamceno) {
    return (
      <PoleKarta ikona={ikona} nadpis={nadpis} vaha="jemny">
        <p className="text-base whitespace-pre-wrap">
          {zaznam[pole] || <span className="text-text-secondary">— nevyplněno —</span>}
        </p>
      </PoleKarta>
    );
  }

  // Diktát u strojů/materiálu = malá ikona mikrofonu v poli (ne plnošířkové
  // modré CTA) — jediné primární diktovací CTA zůstává u Provedených prací
  // (blueprint 16 §Deník: sjednotit trojí NADIKTOVAT na jedno).
  return (
    <PoleKarta ikona={ikona} nadpis={nadpis} vaha="jemny">
      <div className="flex items-start gap-2">
        <Textarea
          aria-label={nadpis}
          className="flex-1"
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => {
            if (text !== (zaznam[pole] ?? '')) void uloz({ [pole]: text || null });
          }}
          placeholder={placeholder}
        />
        {diktovani.podporovano ? (
          <button
            type="button"
            aria-label={`Nadiktovat — ${nadpis}`}
            aria-pressed={diktovani.diktuji}
            onClick={diktovani.prepni}
            className={`flex min-h-12 min-w-12 shrink-0 items-center justify-center rounded-lg border-2 text-xl ${
              diktovani.diktuji
                ? 'border-text-heading bg-text-heading text-white'
                : 'border-border bg-surface shadow-sm'
            }`}
          >
            {diktovani.diktuji ? (
              <Square size={16} fill="currentColor" aria-hidden="true" />
            ) : (
              <Mic size={18} strokeWidth={2} aria-hidden="true" />
            )}
          </button>
        ) : null}
      </div>
      {diktovani.prubezny ? (
        <p aria-live="polite" className="mt-1 text-base text-text-secondary italic">
          {diktovani.prubezny}…
        </p>
      ) : null}
      {nabidka.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {nabidka.map((fraze) => (
            <button
              key={fraze}
              type="button"
              className="min-h-12 rounded-full border-2 border-border-strong bg-bg px-4 text-base font-semibold"
              onClick={() => vlozFrazi(fraze)}
            >
              + {fraze}
            </button>
          ))}
        </div>
      )}
    </PoleKarta>
  );
}

// ---------------------------------------------------------------------------
// Provedené práce: NADIKTOVAT (Web Speech cs-CZ) / psaní / kopie včerejška.
// ---------------------------------------------------------------------------

function PraceKarta({
  zaznam,
  uloz,
  poNahrani,
  naChybu,
}: {
  zaznam: DenikZaznamDetail;
  uloz: (telo: Record<string, unknown>) => Promise<void>;
  poNahrani: () => void;
  naChybu: (ch: string | null) => void;
}) {
  const [text, setText] = useState(zaznam.provedenePrace ?? '');

  const diktovani = useDiktovani((useky) => {
    setText((puvodni) => {
      const dalsi = puvodni ? `${puvodni.trimEnd()} ${useky}` : useky;
      void uloz({ provedenePrace: dalsi });
      return dalsi;
    });
  });

  if (zaznam.uzamceno) {
    return (
      <PoleKarta
        vaha="hlavni"
        ikona={<Hammer size={18} strokeWidth={1.75} />}
        nadpis="Provedené práce"
      >
        <p className="text-base whitespace-pre-wrap">
          {zaznam.provedenePrace || <span className="text-text-secondary">— nevyplněno —</span>}
        </p>
        <FotkySekce zaznam={zaznam} poNahrani={poNahrani} naChybu={naChybu} />
      </PoleKarta>
    );
  }

  return (
    // Hero pole dne: to hlavní, proč mistr deník otevírá. Silnější rám, ikona,
    // větší psací plocha a jediné plnošířkové NADIKTOVAT.
    <PoleKarta
      vaha="hlavni"
      ikona={<Hammer size={18} strokeWidth={1.75} />}
      nadpis="Provedené práce"
      napoveda={<p>Nejdůležitější zápis dne — co se dnes na stavbě udělalo. Můžete i diktovat.</p>}
    >
      {/* Velká psací plocha — hlavní práce dne; diktát je kompaktní doplněk. */}
      <Textarea
        aria-label="Provedené práce"
        rows={8}
        className="min-h-52 text-lg"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          if (text !== (zaznam.provedenePrace ?? '')) void uloz({ provedenePrace: text || null });
        }}
        placeholder="Co se dnes udělalo…"
      />
      {diktovani.prubezny ? (
        <p aria-live="polite" className="mt-1 text-base text-text-secondary italic">
          {diktovani.prubezny}…
        </p>
      ) : null}
      <div className="mt-2">
        {diktovani.podporovano ? (
          <Btn varianta={diktovani.diktuji ? 'primary' : 'outline'} onClick={diktovani.prepni}>
            {diktovani.diktuji ? (
              <>
                <Square size={14} fill="currentColor" aria-hidden="true" /> ZASTAVIT DIKTÁT
              </>
            ) : (
              <>
                <Mic size={18} strokeWidth={2} aria-hidden="true" /> NADIKTOVAT
              </>
            )}
          </Btn>
        ) : (
          <Btn varianta="outline" disabled title="tento prohlížeč diktát neumí — pište">
            <Mic size={18} strokeWidth={2} aria-hidden="true" /> NADIKTOVAT
          </Btn>
        )}
      </div>

      {/* Fotky přímo u zápisu — mistr vyfotí a podle fotek sepíše práce. */}
      <FotkySekce zaznam={zaznam} poNahrani={poNahrani} naChybu={naChybu} />
    </PoleKarta>
  );
}

// ---------------------------------------------------------------------------
// Fotky dne (C1–C3): capture i galerie, smyčka pro víc souborů, popis, fronta.
// ---------------------------------------------------------------------------

/**
 * Fotky přímo u zápisu prací (uvnitř hero karty): mistr vyfotí a podle fotek
 * sepíše, co se dělalo. VYFOTIT = capture, PŘIDAT FOTO = galerie (víc souborů).
 */
function FotkySekce({
  zaznam,
  poNahrani,
  naChybu,
}: {
  zaznam: DenikZaznamDetail;
  poNahrani: () => void;
  naChybu: (ch: string | null) => void;
}) {
  const [cekajici, setCekajici] = useState<File[]>([]);
  const [popis, setPopis] = useState('');
  const [nahravam, setNahravam] = useState(false);

  const vyber = (soubory: FileList | null) => {
    if (!soubory) return;
    setCekajici((c) => [...c, ...Array.from(soubory)]);
  };

  const nahraj = async () => {
    setNahravam(true);
    naChybu(null);
    try {
      for (const f of cekajici) {
        await odesliNeboZarad({
          druh: 'soubor',
          url: `/api/diary/${zaznam.id}/prilohy`,
          soubor: {
            blob: f,
            nazevSouboru: f.name || 'foto.jpg',
            mimeType: f.type || 'image/jpeg',
            popis: popis || undefined,
          },
          popis: `Foto k deníku ${formatDen(zaznam.datum)}`,
        });
      }
      setCekajici([]);
      setPopis('');
      poNahrani();
    } catch (err) {
      naChybu(hlaskaZChyby(err));
    } finally {
      setNahravam(false);
    }
  };

  return (
    <div className="mt-4 border-t border-border pt-3">
      <p className="mb-2 text-sm font-semibold text-text-secondary">
        Fotky k zápisu ({zaznam.prilohy.length})
      </p>
      {zaznam.prilohy.length > 0 && (
        <div className="mb-3 flex gap-2 overflow-x-auto">
          {zaznam.prilohy.map((p) => (
            <figure key={p.id} className="w-24 shrink-0">
              <img
                src={`/api/diary/prilohy/${p.id}/soubor`}
                alt={p.popis ?? p.nazev}
                className="h-24 w-24 rounded-lg border border-border object-cover"
                loading="lazy"
              />
              {p.bezGps ? (
                <figcaption className="text-xs text-warning-text">bez GPS</figcaption>
              ) : null}
            </figure>
          ))}
        </div>
      )}
      {!zaznam.uzamceno ? (
        <>
          {/* VYFOTIT = capture (fotoaparát); PŘIDAT FOTO = galerie (C3, víc souborů). */}
          <div className="grid grid-cols-2 gap-2">
            <label className="flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-lg bg-primary px-3 text-base font-bold text-white hover:bg-primary-hover">
              <Camera size={18} strokeWidth={2} aria-hidden="true" /> VYFOTIT
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="sr-only"
                aria-label="Vyfotit"
                onChange={(e) => {
                  vyber(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
            <label className="flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-lg border border-border bg-surface px-3 text-base font-bold shadow-sm hover:bg-bg">
              <Images size={18} strokeWidth={1.75} aria-hidden="true" /> PŘIDAT FOTO
              <input
                type="file"
                accept="image/*"
                multiple
                className="sr-only"
                aria-label="Přidat foto z galerie"
                onChange={(e) => {
                  vyber(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
          {cekajici.length > 0 && (
            <div className="mt-3 flex flex-col gap-2">
              <p className="text-base">
                Vybráno {cekajici.length}{' '}
                {cekajici.length === 1 ? 'foto' : cekajici.length < 5 ? 'fotky' : 'fotek'} — lze
                přidat další, nebo rovnou nahrát.
              </p>
              <Input
                aria-label="Popis fotek"
                placeholder="Popis (nepovinné)…"
                value={popis}
                onChange={(e) => setPopis(e.target.value)}
              />
              <VelkeTlacitko onClick={() => void nahraj()} disabled={nahravam}>
                {nahravam ? 'Nahrávám…' : `NAHRÁT (${cekajici.length})`}
              </VelkeTlacitko>
            </div>
          )}
        </>
      ) : (
        <p className="text-sm text-text-secondary">
          Den je podepsaný — nové foto patří k nálezu nebo opravnému záznamu.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Opravný záznam (B2) — jediná cesta po podpisu; původní zůstává čitelný.
// ---------------------------------------------------------------------------

function OpravnyZaznam({
  zaznam,
  naChybu,
}: {
  zaznam: DenikZaznamDetail;
  naChybu: (ch: string | null) => void;
}) {
  const [oteviram, setOteviram] = useState(false);
  const [text, setText] = useState('');
  const navigate = useNavigate();

  const zaloz = useMutation({
    mutationFn: () =>
      api.post<DenikZaznamDetail>(`/api/diary/${zaznam.id}/correction`, {
        provedenePrace: text,
      }),
    onSuccess: (novy) => navigate(`/teren/denik/${novy.id}`),
    onError: (err) => naChybu(hlaskaZChyby(err)),
  });

  if (!oteviram) {
    return (
      <VelkeTlacitko varianta="secondary" onClick={() => setOteviram(true)}>
        OPRAVNÝ ZÁZNAM
      </VelkeTlacitko>
    );
  }
  return (
    <PoleKarta
      ikona={<Pencil size={16} strokeWidth={1.75} />}
      nadpis={`Opravný záznam ke dni ${formatDen(zaznam.datum)}`}
    >
      <Textarea
        aria-label="Text opravy"
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Co se opravuje…"
      />
      <div className="mt-3 grid grid-cols-2 gap-3">
        <VelkeTlacitko disabled={!text.trim() || zaloz.isPending} onClick={() => zaloz.mutate()}>
          {zaloz.isPending ? 'Zakládám…' : 'Založit opravu'}
        </VelkeTlacitko>
        <VelkeTlacitko varianta="secondary" onClick={() => setOteviram(false)}>
          Zrušit
        </VelkeTlacitko>
      </div>
    </PoleKarta>
  );
}
